import { useState, useCallback } from "react";
import { Box } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import {
  BUTTON_DOWNLOAD,
  BUTTON_SAVE_CONTENT,
  MODAL_IMAGE_EDIT_SAVE_ALERT_DESCRIPTION,
  MODAL_IMAGE_EDIT_REPLACE_BUTTON,
  MODAL_IMAGE_EDIT_SAVE_ALERT_TITLE,
  MODAL_IMAGE_EDIT_SAVE_AS_NEW_BUTTON,
} from "../../../../i18n/keysTranslations";
import { startGetAwsDownloadUrl } from "../../../../actions/user";
import { ConfirmAlert } from "../../../Alerts/Alerts";
import Button from "../../../Buttons/Button";

const SaveContentActions = ({
  disabled = false,
  showSaveAlert = true,
  showDownload = false,
  downloadUrl = null,
  contentUrl = null,
  onSave = () => {},
  onCloseAfterSave,
  saveAlertTitleKey = MODAL_IMAGE_EDIT_SAVE_ALERT_TITLE,
  saveAlertDescriptionKey = MODAL_IMAGE_EDIT_SAVE_ALERT_DESCRIPTION,
  replaceButtonKey = MODAL_IMAGE_EDIT_REPLACE_BUTTON,
  saveAsNewButtonKey = MODAL_IMAGE_EDIT_SAVE_AS_NEW_BUTTON,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const buildSavePayload = useCallback(
    (mode) => {
      const payload = { mode };
      if (contentUrl) {
        payload.url = contentUrl;
      }
      return payload;
    },
    [contentUrl]
  );

  const onDownload = useCallback(
    async (event) => {
      event?.stopPropagation?.();
      if (!downloadUrl) return;

      try {
        setIsDownloading(true);
        await dispatch(startGetAwsDownloadUrl(downloadUrl));
      } catch (error) {
        console.error("Error downloading image:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [dispatch, downloadUrl]
  );

  const onSaveContent = useCallback(
    async (event) => {
      event?.stopPropagation?.();
      if (disabled) return;

      try {
        setIsSaving(true);

        if (!showSaveAlert) {
          await onSave(buildSavePayload("replace"));
          onCloseAfterSave?.();
          return;
        }

        const response = await ConfirmAlert({
          title: t(saveAlertTitleKey),
          text: t(saveAlertDescriptionKey),
          showCancelButton: true,
          showThirdButton: true,
          confirmButtonText: t(replaceButtonKey),
          thirdButtonText: t(saveAsNewButtonKey),
          confirmButtonVariant: "secondary",
          cancelButtonVariant: "secondary",
          denyButtonVariant: "secondary",
          width: "550px",
        });

        if (response?.isConfirmed) {
          await onSave(buildSavePayload("replace"));
        } else if (response?.isDenied) {
          await onSave(buildSavePayload("new"));
        } else {
          return;
        }

        onCloseAfterSave?.();
      } catch (error) {
        console.error("Error saving content:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      buildSavePayload,
      disabled,
      onCloseAfterSave,
      onSave,
      replaceButtonKey,
      saveAlertDescriptionKey,
      saveAlertTitleKey,
      saveAsNewButtonKey,
      showSaveAlert,
      t,
    ]
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent: "center",
        alignItems: "center",
        p: 4,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        flexShrink: 0,
      }}
    >
      {showDownload && downloadUrl ? (
        <Button
          color="secondary"
          onClick={onDownload}
          disabled={isDownloading}
          loading={isDownloading}
          startIcon={<DownloadIcon />}
          sx={{ minWidth: 140 }}
        >
          {t(BUTTON_DOWNLOAD)}
        </Button>
      ) : null}

      <Button
        variant="contained"
        color="primary"
        onClick={onSaveContent}
        disabled={disabled || isSaving}
        loading={isSaving}
        sx={{ minWidth: 180 }}
      >
        {t(BUTTON_SAVE_CONTENT)}
      </Button>
    </Box>
  );
};

export default SaveContentActions;
